import {
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Form, Input, message, Skeleton, Spin } from 'antd';
import { RuleObject } from 'antd/es/form';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAddresses } from '@/hooks/useAddresses';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { CustomSelect } from '@/components/shared/CustomSelect';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { IntlTelInput } from '@/components/shared/IntlTelInput';
import { notify } from '@/components/shared/notify';

import { getGlobalCountry } from '@/atoms/countryState';
import { user } from '@/atoms/user';
import { createAddressService, updateAddress } from '@/services/Address';
import {
  executeOrganizationPurchase,
  getWalletSummary,
  requestOrganizationTopUp,
  WalletSummary,
} from '@/services/Organization';
import { getProductDetails } from '@/services/Products';
import { getUserProfile } from '@/services/user';
import { Address, AddressPayload } from '@/utils/interfaces/Manage-Addresses';
import { IProduct } from '@/utils/interfaces/Product';
import { User } from '@/utils/interfaces/User';

interface CartContactFormState {
  visibleLogin: boolean;
  visibleForgotPassword: boolean;
  selectedAddress: Address | null;
  isChanged: boolean;
  updateLoading: boolean;
  paymentOption?: 'razorpay' | 'cash_on_delivery' | 'wallet';
  error: string | null;
  loginError: string;
  checkEmailLoading: boolean;
  checkEmailError: string;
  phoneError?: string;
}

interface IFormData {
  fullname: string;
  email: string;
  postalcode: string;
  phone: string;
  country: string;
  city: string;
  state: string;
  address: string;
  password: string;
  parentEmail?: string;
  parentName?: string;
  parentPhone?: string;
  phoneCountryCode?: string;
  phonePrefix?: string;
}

const CorporateCheckout = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const productId = searchParams.get('productId');

  const {
    getAllCountries,
    countries,
    getStates,
    states,
    cities,
    getCities,
    fieldLoading,
    getAllAddresses,
    address,
    loading,
  } = useAddresses();

  const screens = useBreakpoint();

  const [state, setState] = useState<CartContactFormState>({
    visibleLogin: false,
    visibleForgotPassword: false,
    selectedAddress: null,
    isChanged: false,
    updateLoading: false,
    paymentOption: 'wallet',
    error: null,
    loginError: '',
    checkEmailLoading: false,
    checkEmailError: '',
    phoneError: '',
  });
  const globalCountry = getGlobalCountry();
  const { selectedAddress, isChanged } = state;
  const [formData, setFormData] = useState<IFormData>({
    fullname: '',
    email: '',
    postalcode: '',
    phone: '',
    country: '',
    city: '',
    state: '',
    address: '',
    password: '',
    phoneCountryCode: '',
    phonePrefix: '',
  });
  const [form] = Form.useForm();
  const userData: User | undefined = useRecoilValue(user);
  const [product, setProduct] = useState<IProduct>();
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [, setUserData] = useRecoilState(user);

  const [requestTopUpLoading, setRequestTopUpLoading] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);

  const unitPrice =
    (product as any)?.organization_pricing?.discounted_price ??
    (product as any)?.i18n_attributes?.unit_price ??
    product?.main_price ??
    (product as any)?.unit_price ??
    0;

  const regularPrice =
    (product as any)?.organization_pricing?.original_price ??
    (product as any)?.i18n_attributes?.regular_price ??
    product?.stroked_price ??
    (product as any)?.regular_price ??
    0;

  const rawBalance =
    (userData as any)?.organization?.wallet_balance ??
    wallet?.balance ??
    0;

  // The organization wallet_balance from API is in paise (e.g. 10000 = ₹100)
  const availableBalance = Number(rawBalance) / 100;
  const isInsufficientBalance = Boolean(
    userData?.organization && availableBalance < Number(unitPrice),
  );
  const userRole = (userData as any)?.organization?.role || 'member';
  const isAdmin = userRole === 'admin';
  const deficitAmount = Math.max(0, Number(unitPrice) - availableBalance);

  useEffect(() => {
    if (!productId) return;
    try {
      const storedExpiry = localStorage.getItem(
        `corporate_topup_cooldown_${productId}`,
      );
      if (storedExpiry) {
        const remainingMs = Number(storedExpiry) - Date.now();
        if (remainingMs > 0) {
          setCountdown(Math.ceil(remainingMs / 1000));
        } else {
          localStorage.removeItem(`corporate_topup_cooldown_${productId}`);
        }
      }
    } catch {
      // ignore
    }
  }, [productId]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          try {
            if (productId) {
              localStorage.removeItem(`corporate_topup_cooldown_${productId}`);
            }
          } catch {
            // ignore
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, productId]);

  const handleRequestTopUp = async () => {
    if (!productId || countdown > 0 || requestTopUpLoading) return;
    setRequestTopUpLoading(true);
    try {
      const res = await requestOrganizationTopUp(productId as string);
      message.success(
        res?.message || 'Top-up request sent to admin successfully!',
      );
      setCountdown(60);
      try {
        localStorage.setItem(
          `corporate_topup_cooldown_${productId}`,
          String(Date.now() + 60000),
        );
      } catch {
        // ignore
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.message ||
          'Failed to send top-up request. Please try again.',
      );
    } finally {
      setRequestTopUpLoading(false);
    }
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    form.setFieldsValue({ [name]: value });
  };

  const handleSelectChange = (value: string | number, name: string) => {
    if (name === 'country') {
      setFormData({
        ...formData,
        [name as string]: value,
        state: '',
        city: '',
      });
      setState((prev) => ({ ...prev, isChanged: true }));
    } else if (name === 'state') {
      setFormData({ ...formData, [name as string]: value, city: '' });
      setState((prev) => ({ ...prev, isChanged: true }));
    } else {
      setFormData({ ...formData, [name as string]: value });
      setState((prev) => ({ ...prev, isChanged: true }));
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string | number },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const affixContent = (
    <>
      <div
        className={clsx(
          !screens.lg && 'mb-0',
          'bg-[#101010] border border-[#1E1E1E] rounded-2xl w-full overflow-hidden shadow-xl',
        )}
      >
        <div className='flex gap-y-2 flex-col pt-5 md:pt-6 px-6'>
          <span className='text-base md:text-lg font-bold tracking-tight text-white'>
            Order Summary
          </span>
          <div className='flex flex-col gap-y-4 mt-3 md:mt-4'>
            <ShouldRender check={!loading}>
              <div className='flex items-center gap-4'>
                <div
                  className={clsx(
                    'p-1.5 items-center justify-center',
                    'w-16 h-16 flex bg-[#1A1A1A] border border-[#282828] rounded-xl flex-shrink-0 overflow-hidden',
                  )}
                >
                  <Image
                    src={product?.thumbnail_image ?? ''}
                    alt='Product'
                    width={160}
                    height={160}
                    className='object-contain w-full h-full'
                  />
                </div>
                <div className='flex justify-between items-start gap-x-3 w-full'>
                  <div className='flex flex-col gap-y-0.5 text-left'>
                    <span className='text-xs md:text-sm font-semibold leading-snug text-white line-clamp-2'>
                      {product?.name}
                    </span>
                    <span className='font-normal text-[11px] text-grayZl-400'>
                      {product?.book_type || 'Corporate Edition'}
                    </span>
                  </div>
                  <div className='flex justify-end flex-col items-end flex-shrink-0'>
                    {Number(regularPrice) > 0 && (
                      <div className='font-light text-[11px] md:text-xs whitespace-nowrap text-grayZl-500 line-through'>
                        ₹ {Number(regularPrice).toLocaleString('en-IN')}
                      </div>
                    )}
                    <div className='font-bold text-sm md:text-base text-white whitespace-nowrap'>
                      ₹ {Number(unitPrice).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
              </div>
            </ShouldRender>

            <ShouldRender check={loading}>
              <div className='flex gap-x-4 overflow-y-auto'>
                <Skeleton.Button
                  active
                  shape='square'
                  size='small'
                  className='rounded-md w-[52px] h-[52px]'
                />
                <div className='w-full flex flex-col'>
                  <span className='w-1/3'>
                    <Skeleton.Button
                      active
                      shape='square'
                      size='small'
                      className='h-3 w-full'
                    />
                  </span>
                  <Skeleton.Input
                    active
                    size='small'
                    style={{ width: '80%' }}
                    className='h-5'
                  />
                </div>
                <div className='text-sm flex w-1/2 items-center text-textDarkGrayColor'>
                  <Skeleton.Button
                    active
                    size='small'
                    className='h-full py-2'
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>

        <div className='flex flex-col'>
          <div className='w-full border-t border-[#1C1C1C] my-4' />
          <div className='flex justify-between items-center px-6 pb-5'>
            <p className='text-sm text-white font-medium'>To Pay</p>
            <div className='flex items-center gap-2'>
              <p className='text-2xl md:text-3xl font-extrabold text-white tracking-tight'>
                ₹ {Number(unitPrice).toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Insufficient Balance Section */}
          {isInsufficientBalance && (
            <div>
              {isAdmin ? (
                /* Admin View */
                <div className='bg-[#1C1408] border-t border-b border-[#3A2508] px-6 py-4 text-left'>
                  <div className='flex items-center gap-2 text-amber-500 font-bold text-sm'>
                    <ExclamationCircleFilled className='text-amber-500 text-sm' />
                    <span>Insufficient balance</span>
                  </div>
                  <p className='text-xs text-grayZl-300 mt-1 leading-relaxed'>
                    You need ₹{deficitAmount.toLocaleString('en-IN')} more to complete this payment.
                  </p>
                </div>
              ) : (
                /* Member View */
                <div className='bg-[#141414] border-t border-b border-[#222222] px-6 py-4 text-left'>
                  <div className='flex items-center gap-2 text-gray-200 font-semibold text-sm'>
                    <InfoCircleOutlined className='text-gray-400 text-sm' />
                    <span>Insufficient balance</span>
                  </div>
                  <p className='text-xs text-grayZl-400 mt-1 leading-relaxed'>
                    Please ask your admin to add balance to proceed with this purchase.
                  </p>
                </div>
              )}

              <div className='p-6'>
                {isAdmin ? (
                  <button
                    type='button'
                    onClick={() => router.push('/corporate/wallet')}
                    className='w-full h-12 bg-[#FF5612] hover:bg-[#E54D10] text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center cursor-pointer shadow-lg active:scale-98'
                  >
                    Add Balance
                  </button>
                ) : (
                  <button
                    type='button'
                    disabled={requestTopUpLoading || countdown > 0}
                    onClick={handleRequestTopUp}
                    className='w-full h-12 bg-[#FF5612] hover:bg-[#E54D10] disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center cursor-pointer shadow-lg active:scale-98'
                  >
                    {requestTopUpLoading ? (
                      <>
                        <LoadingOutlined className='mr-2 text-sm' />
                        <span>Sending Request...</span>
                      </>
                    ) : countdown > 0 ? (
                      `Request Sent (${countdown}s)`
                    ) : (
                      'Request Money'
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const handleInput = useCallback(
    (fieldName: string, notIndia: boolean) =>
      async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        const cleanedValue = value.replace(/\s/g, '');
        const sanitizedValue = notIndia
          ? cleanedValue.replace(/\D/g, '')
          : cleanedValue;

        setFormData({
          ...formData,
          [fieldName]: sanitizedValue,
        });
      },
    [formData],
  );

  const getProductsDetailsHandle = async (slug: string) => {
    try {
      const result = await getProductDetails(slug);
      setProduct(result.data || result);
    } catch (error) {
      console.error(error);
    }
  };

  const getValue = (
    data: { label: string; value: string | number }[] | undefined,
    key: string,
  ) =>
    typeof formData[key as keyof typeof formData] === 'number'
      ? formData[key as keyof typeof formData]
      : String(
        data?.find(
          (item) => item.label === formData[key as keyof typeof formData],
        )?.value as string,
      );

  const submitHandle = async () => {
    try {
      if (!productId) {
        notify('Product ID is missing', 'error');
        return;
      }

      const validationErrors = {};
      await form.validateFields();

      if (Object.values(validationErrors).some((error) => error)) {
        return;
      }

      setSubmitLoading(true);
      const selectedState = getValue(states, 'state');
      const selectedCity = getValue(cities, 'city');
      const selectedCountry = getValue(countries, 'country');

      let addr = address;

      const data = {
        user_id: userData?.id,
        name: userData?.name as string,
        address: formData?.address,
        country_id: selectedCountry as string,
        city_id: selectedCity as string,
        state_id: selectedState as string,
        postal_code: formData?.postalcode,
        phone: formData?.phone as string,
        country_code: formData?.phonePrefix,
        iso2: formData?.phoneCountryCode,
      };

      if (!addr || addr.length === 0) {
        const result = await createAddressService({
          ...data,
          user_id: data.user_id || 0,
          set_default: 0,
        });

        addr = [result.data];
      }

      if (isChanged && addr?.[0]?.id) {
        await updateAddress({
          ...data,
          id: Number(addr?.[0]?.id),
          set_default: 0,
        } as AddressPayload);
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      const result = await executeOrganizationPurchase({
        product_id: parseInt(productId),
        address_id: addr?.[0]?.id,
        quantity: 1,
      });

      const userProfileRes = await getUserProfile();
      if (userProfileRes?.data) {
        setUserData(userProfileRes.data);
      }

      const orderId =
        result?.data?.order_id ||
        result?.data?.id ||
        result?.order_id ||
        `CORP-${Date.now()}`;

      router.replace(`/order-successful?orderId=${orderId}`);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleValuesChange = (changedValues: any) => {
    setFormData((prevData) => ({
      ...prevData,
      ...changedValues,
    }));
  };

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    setFormData((prev) => ({
      ...prev,
      phone: cleanedValue,
      phoneCountryCode,
      phonePrefix,
    }));

    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        '+' + phonePrefix + cleanedValue,
      );

      if (!cleanedValue.trim()) {
        const errorMsg = 'Phone number is required';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else if (!parsedPhoneNumber) {
        const errorMsg = 'Invalid phone number format';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else if (!parsedPhoneNumber.isValid()) {
        const errorMsg =
          'Invalid phone number. Please check the number and try again.';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else {
        setState((prev) => ({
          ...prev,
          phoneError: '',
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [],
          },
        ]);
      }
    } catch (error) {
      const errorMsg =
        error instanceof ParseError
          ? error.message
          : 'An error occurred while validating the phone number. Please try again.';

      setState((prev) => ({
        ...prev,
        phoneError: errorMsg,
      }));
      form.setFields([
        {
          name: 'phone',
          errors: [errorMsg],
        },
      ]);
    }
  };

  useEffect(() => {
    getAllCountries();
  }, [selectedAddress]);

  useEffect(() => {
    if (formData?.country && countries) {
      const selectedCountry =
        typeof formData?.country === 'string'
          ? String(
            countries?.find((item) => item.label === formData.country)?.value,
          )
          : formData?.country;
      if (selectedCountry && selectedCountry !== 'undefined') {
        getStates(selectedCountry);
      }
    }
  }, [formData?.country, countries, selectedAddress]);

  useEffect(() => {
    const finalizeFormSetup = async () => {
      if (userData) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          email: userData.email,
        }));
        form.setFieldValue('email', userData.email);
        getAllAddresses();
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        country: 'India',
      }));
      form.setFieldValue('country', 'India');
    };

    finalizeFormSetup();
  }, [userData]);

  useEffect(() => {
    if (formData?.state && states) {
      const selectedState =
        typeof formData?.state === 'string'
          ? String(states?.find((item) => item.label === formData.state)?.value)
          : formData?.state;
      if (selectedState && selectedState !== 'undefined') {
        getCities(selectedState);
      }
    }
  }, [formData?.state, states, selectedAddress]);

  useEffect(() => {
    if (userData && address) {
      if (address.length > 0) {
        setFormData((prevData) => ({
          ...prevData,
          ...address[0],
          password: address[0]?.password || '',
        }));

        Object.entries(address[0] as object).forEach(([key, value]) => {
          form.setFieldsValue({ [key]: value });
        });
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        email: userData.email,
      }));
      form.setFieldsValue({ email: userData.email });
    }
  }, [selectedAddress, userData, address, form]);

  useEffect(() => {
    if (productId) {
      getProductsDetailsHandle(productId as string);
    }
    getWalletSummary().then(setWallet).catch(() => { });
  }, [productId]);

  useEffect(() => {
    if (address?.length) {
      const isAddressChanged =
        address?.[0] &&
        Object.entries(address?.[0]).some(
          ([key, value]) => value !== (formData as any)[key],
        );
      setState((prevState) => ({
        ...prevState,
        isChanged: isAddressChanged as boolean,
      }));
    }
  }, [address, formData]);

  return (
    <div className='lg:grid-cols-3 grid w-full relative mt-20'>
      <div className='col-span-2 lg:border-r-2 border-borderDarkGray'>
        <div className='px-4 flex flex-col gap-y-4'>
          <Form
            form={form}
            onFinish={submitHandle}
            layout='vertical'
            initialValues={formData}
            onValuesChange={handleValuesChange}
          >
            <div className='flex md:px-8 flex-col gap-y-5 text-fadedWhite'>
              {/* Billing Address Section */}
              <div className='bg-cartModalFormBg py-4 px-4 md:px-5 rounded-lg'>
                <span className='flex justify-between gap-x-2 md:gap-x-5 items-center pb-4 md:pb-5'>
                  <div className='flex gap-x-4'>
                    <p className='text-base font-semibold whitespace-nowrap'>
                      Delivery / Billing Address
                    </p>
                    <ShouldRender check={loading}>
                      <span>
                        <Spin size={screens.md ? 'default' : 'small'} />
                      </span>
                    </ShouldRender>
                  </div>
                </span>
                <div>
                  <Form.Item
                    name='address'
                    valuePropName='address'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const addressValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (addressValue) {
                            if (addressValue.length <= 190) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  'Address exceeds maximum length of 190 characters!',
                                ),
                              );
                            }
                          } else {
                            return Promise.reject(
                              new Error('Please enter your address!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <Input.TextArea
                      size='large'
                      name='address'
                      value={formData.address}
                      onChange={(e) => handleChange(e)}
                      minLength={6}
                      placeholder='Type your complete Address'
                      className='border text-base focus-within:bg-bgColor z-20 placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray text-white bg-bgColor customInputBlack makeBgBlack'
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  name='phone'
                  valuePropName='phone'
                  rules={[
                    {
                      validator: (_) => {
                        if (state.phoneError) {
                          return Promise.reject(new Error(state.phoneError));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <IntlTelInput
                    phone={formData.phone || ''}
                    phoneCountryCode={
                      formData.phoneCountryCode ||
                      globalCountry?.countryCode?.toLowerCase() ||
                      'in'
                    }
                    onChange={({ phone, phoneCountryCode, phonePrefix }) => {
                      const cleanedPhone = phone.replace(/^0+/, '');
                      handlePhoneInput({
                        phone: cleanedPhone,
                        phoneCountryCode,
                        phonePrefix,
                      });
                      form.setFieldsValue({ phone });
                    }}
                    id='phone'
                  />
                </Form.Item>
                <Form.Item
                  name='country'
                  valuePropName='country'
                  rules={[
                    {
                      validator: (_: RuleObject, value: any) => {
                        const countryValue =
                          value && typeof value === 'object'
                            ? value.target.value
                            : value;
                        if (countryValue) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Please select your country!'),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <CustomSelect
                    value={formData.country}
                    onChange={(value) => {
                      handleSelectChange(value, 'country');
                    }}
                    options={countries}
                    placeholder='Select Country'
                    loading={fieldLoading.country}
                    filterOption={filterOption}
                  />
                </Form.Item>
                <Form.Item
                  name='postalcode'
                  valuePropName='postalcode'
                  rules={[
                    {
                      validator: (_, value) => {
                        const enteredPostalCode =
                          value && typeof value === 'object'
                            ? value.target.value
                            : value;

                        const cleanedPostalCode = enteredPostalCode.replace(
                          /\s/g,
                          '',
                        );

                        const countryIsIndia =
                          formData.country === 'India' ||
                          Number(formData.country) === 101;
                        const isValidPostalCodeIndia = /^[0-9]{6}$/.test(
                          cleanedPostalCode,
                        );

                        if (countryIsIndia && isValidPostalCodeIndia) {
                          return Promise.resolve();
                        } else if (
                          !countryIsIndia &&
                          cleanedPostalCode &&
                          cleanedPostalCode.trim() !== ''
                        ) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Please enter a valid postal code!'),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    size='large'
                    name='postalcode'
                    inputMode='numeric'
                    placeholder='Postal code'
                    value={formData.postalcode}
                    className='border text-base focus-within:bg-bgColor z-20 placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray text-white bg-bgColor customInputBlack makeBgBlack'
                    onChange={handleInput(
                      'postalcode',
                      formData.country === 'India' ||
                      Number(formData.country) === 101,
                    )}
                  />
                </Form.Item>
                <div className='grid sm:grid-cols-2 sm:gap-4'>
                  <Form.Item
                    name='state'
                    valuePropName='state'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const stateValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (stateValue) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error('Please select your state!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <CustomSelect
                      value={formData.state === '' ? undefined : formData.state}
                      onChange={(value) => {
                        handleSelectChange(value, 'state');
                      }}
                      options={states}
                      placeholder='Select State'
                      disabled={!formData?.country}
                      loading={fieldLoading.state}
                      name='state'
                      filterOption={filterOption}
                    />
                  </Form.Item>

                  <Form.Item
                    name='city'
                    valuePropName='city'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const cityValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (cityValue) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error('Please select your city!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <CustomSelect
                      value={formData.city === '' ? undefined : formData.city}
                      onChange={(value) => {
                        handleSelectChange(value, 'city');
                      }}
                      options={cities}
                      placeholder='Select City'
                      loading={fieldLoading.city}
                      disabled={!formData?.state}
                      filterOption={filterOption}
                      name='city'
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
      <div className='lg:min-h-[93vh] pt-5 lg:pt-0 lg:col-span-1 col-span-2 border-borderDarkGray'>
        <div className='text-fadedWhite px-4 lg:px-0 w-full flex flex-col gap-y-2 rounded-md cardShadow'>
          <div className='flex flex-col mb-0 md:px-8 lg:px-4 w-full'>
            {affixContent}
          </div>
          {!isInsufficientBalance && (
            <div className='flex flex-col mb-8 md:px-8 lg:px-4 w-full'>
              <ButtonDefault
                size={0}
                rounded={false}
                roundedSm={true}
                enableScaling={false}
                disabled={submitLoading}
                loading={submitLoading}
                shimmerButton={true}
                onClick={submitHandle}
                variant={ButtonVariants.PRIMARY}
                className='py-3'
              >
                Proceed to Pay
              </ButtonDefault>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateCheckout;
